import { memo, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styled from "@emotion/styled";

import type { ClassificationConfig } from "@atlas-int/data";
import {
  border,
  borderRadius,
  brand,
  fontSize,
  layout,
  lineHeight,
  shadow,
  spacing,
  surface,
  text,
  zIndex as zIndexTokens,
} from "@atlas-int/tokens";
import Button from "@atlas-int/ui/Button";
import ButtonGroup from "@atlas-int/ui/ButtonGroup";
import InfoTooltip from "@atlas-int/ui/InfoTooltip";

import { LabelContainer, SegmentContainer } from "./ControlsGrid";

const ToggleGroupContainer = styled(SegmentContainer)`
  min-width: 0;
`;

const SizedButtonGroup = styled(ButtonGroup)`
  height: ${layout.controlsHeight};
  box-sizing: border-box;
  border-color: ${border.control};

  & > button + button {
    border-color: ${border.control};
  }
`;

const SelectorButton = styled(Button)<{ selected?: boolean }>`
  ${({ selected }) =>
    selected &&
    `
    background-color: ${brand.dark};
    color: ${text.onDark};

    &:hover:not(:disabled) {
      background-color: ${brand.dark};
    }
  `}
`;

const GroupRow = styled.div`
  display: inline-flex;
`;

const DateRangeTooltip = styled.div`
  position: fixed;
  max-width: 220px;
  font-size: ${fontSize.xs};
  line-height: ${lineHeight.normal};
  color: ${text.primary};
  background-color: ${surface.white};
  white-space: normal;
  padding: ${spacing[3]} ${spacing[4]};
  box-sizing: border-box;
  z-index: ${zIndexTokens.overlay};
  border-radius: ${borderRadius.base};
  border: 1px solid ${border.subtle};
  box-shadow: ${shadow.sm};
  transform: translateX(-50%);
  cursor: default;
  pointer-events: none;

`;

interface ProductClassToggleGroupProps {
  /** Currently selected product class */
  value: string;
  /** Called when a product class is selected */
  onChange: (productClass: string) => void;
  /** Available classification configurations */
  classifications: ClassificationConfig[];
  /** Function to determine if a classification should be disabled */
  isDisabled?: (config: ClassificationConfig) => boolean;
}

/**
 * Product Class toggle group -- renders HS92 | HS12 | HS22 | SITC as
 * attached selector buttons in the control bar.
 *
 * All state is managed externally via value/onChange props.
 */
const ProductClassToggleGroup = ({
  value: productClass,
  onChange,
  classifications,
  isDisabled,
}: ProductClassToggleGroupProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const handleSelect = (classValue: string) => {
    if (productClass === classValue) return;
    onChange(classValue);
  };

  const handleMouseEnter = useCallback((index: number) => {
    const el = buttonRefs.current[index];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom + 6,
      left: rect.left + rect.width / 2,
    });
    setHoveredIndex(index);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  const hoveredConfig =
    hoveredIndex !== null ? classifications[hoveredIndex] : null;
  const hoveredYearRange = hoveredConfig?.yearRange;

  return (
    <ToggleGroupContainer>
      <LabelContainer>
        <InfoTooltip
          label="Product Class"
          text="Visualize data by a standardized product classification system. Year ranges, product naming and product availability vary per classification."
          placement="below"
        />
      </LabelContainer>
      <GroupRow>
        <SizedButtonGroup attached>
          {classifications.map((config, index) => {
            const disabled = isDisabled ? isDisabled(config) : false;
            const shortLabel =
              config.value === "SITC" ? "SITC" : config.value;
            return (
              <SelectorButton
                key={config.value}
                ref={(el: HTMLButtonElement | null) => {
                  buttonRefs.current[index] = el;
                }}
                variant="selector"
                size="md"
                selected={productClass === config.value}
                disabled={disabled}
                onClick={() => handleSelect(config.value)}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
              >
                {shortLabel}
              </SelectorButton>
            );
          })}
        </SizedButtonGroup>
      </GroupRow>
      {hoveredIndex !== null &&
        hoveredYearRange &&
        createPortal(
          <DateRangeTooltip
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
          >
            {hoveredYearRange.yearMin}–{hoveredYearRange.yearMax}
          </DateRangeTooltip>,
          document.body,
        )}
    </ToggleGroupContainer>
  );
};

export default memo(ProductClassToggleGroup);
